import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { useServiceToken } from '@/auth/JwtAuthenticator/JwtAuthenticator';
import { AppRequests } from '@/context/AppRequests';
import { ValidReportTypes } from '@/utils/helperTypes';

type ReportTypeProp = ValidReportTypes | null | undefined;

export function useRemouladeReportSnapshot(
  permalink: string,
  // This is used to disable the query for reports that are not monitored.
  isEnabled = true,
  reportType: ReportTypeProp = null
) {
  const { getSnapshot } = useContext(AppRequests).MonitoringRequests;
  const tokenResult = useServiceToken('remoulade');
  let noEntitiesFailureCount = 0;
  const snapshotResult = useQuery({
    queryKey: ['remoulade_report_snapshot', permalink],
    queryFn: async () => {
      const data = await getSnapshot(tokenResult.data?.token, permalink);

      // Polling/retry strategy for property reports, we need to keep fetching the report until
      // the association for the no_entities flag is resolved or we had tried at least 5 times
      if (
        reportType === 'property' &&
        (data?.meta.no_entities === null ||
          data?.meta.no_entities === undefined) &&
        noEntitiesFailureCount < 4
      ) {
        noEntitiesFailureCount += 1;

        throw new Error(
          "Continue polling because of 'no_entities' unprocessed..."
        );
      }

      return data;
    },
    retryDelay: 2000,
    retry: (failureCount: number, error: Error) => {
      // Default retry logic for errors matching 'Report not ready'
      if (error.message.match(/Report not ready/)) {
        return true;
      }

      // Custom retry logic for reportType === 'property' and no_entities === null or undefined
      if (
        reportType === 'property' &&
        error.message.indexOf('Continue polling') !== -1
      ) {
        return true;
      }

      return false;
    },
    enabled: isEnabled && tokenResult.isSuccess,
  });

  const isLoading = tokenResult.isLoading || snapshotResult.isLoading;
  const isError = tokenResult.isError || snapshotResult.isError;
  const isSuccess = tokenResult.isSuccess && snapshotResult.isSuccess;
  const isFetching = tokenResult.isFetching && snapshotResult.isFetching;
  const { data } = snapshotResult;
  const error = tokenResult.error || snapshotResult.error;
  return { isLoading, isError, isSuccess, isFetching, data, error };
}
